import React, { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import MUIDataTable from "mui-datatables";
import { Icon } from "@iconify/react";
import { useNavigate } from "react-router-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Swal from "sweetalert2";
import { getUsers, deleteUser } from "../Redux/Reducers/AuthSlice";
import DefaultAvatar from "../otherImages/default.png";
import { getPaginationTotal, getRowsPerPageOptions } from "../helper/tablePagination";
import useTableUrlState from "../hook/useTableUrlState";
import { getSortOrder, sortTableData } from "../helper/tableSort";

const UsersDataTable = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [startDate, setStartDate] = useState(new Date("2025-01-01"));
  const [endDate, setEndDate] = useState(new Date("2025-12-10"));
  const [
    { filter, searchText, page, rowsPerPage, sortName, sortDirection },
    setTableState,
    getReturnState,
  ] = useTableUrlState({
    filter: "monthly",
    searchText: "",
    page: 0,
    rowsPerPage: 12,
    sortName: "",
    sortDirection: "",
  });

  const {
    users = [],
    loading = false,
    error = null,
    deletingUser = false,
    usersPagination = null,
  } = useSelector(
    (state) =>
      state.auth || {
        users: [],
        loading: false,
        error: null,
        deletingUser: false,
        usersPagination: null,
      },
  );

  useEffect(() => {
    dispatch(getUsers({ page: page + 1, perPage: rowsPerPage }));
  }, [dispatch, page, rowsPerPage]);

  const handleDeleteUser = async (userId, userName) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: `You want to delete user "${userName}"?`,
      icon: "warning",
      confirmButtonColor: "#000",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      try {
        const response = await dispatch(deleteUser(userId)).unwrap();
        if (response?.success) {
          Swal.fire({
            icon: "success",
            title: "Deleted!",
            text: response?.message || "User has been deleted successfully.",
            timer: 2000,
            showConfirmButton: false,
          });
        } else {
          Swal.fire({
            icon: "error",
            title: "Error!",
            text: response?.message || "Failed to delete user.",
          });
        }
      } catch (error) {
        Swal.fire({
          icon: "error",
          title: "Error!",
          text: error || "Something went wrong while deleting the user.",
        });
      }
    }
  };

  const CustomInput = ({ value, onClick }) => (
    <button
      className="btn btn-outline-secondary d-flex align-items-center gap-2"
      onClick={onClick}
    >
      <Icon icon="uil:calender" width="18" />
      <span>{value}</span>
    </button>
  );

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  // Filter data based on filter type, date range, and search
  const filteredData = useMemo(() => {
    if (!Array.isArray(users)) {
      return [];
    }

    let filtered = [...users];

    if (filter === "monthly") {
      const currentMonth = new Date().getMonth();
      const currentYear = new Date().getFullYear();
      filtered = filtered.filter((user) => {
        const userDate = new Date(user.created_at);
        return (
          userDate.getMonth() === currentMonth &&
          userDate.getFullYear() === currentYear
        );
      });
    } else if (filter === "all") {
    } else {
      if (startDate && endDate) {
        filtered = filtered.filter((user) => {
          const userDate = new Date(user.created_at);
          return (
            userDate >= new Date(startDate.setHours(0, 0, 0, 0)) &&
            userDate <= new Date(endDate.setHours(23, 59, 59, 999))
          );
        });
      }
    }

    // Search filter
    if (searchText) {
      filtered = filtered.filter((user) => {
        const userName =
          user.first_name && user.last_name
            ? `${user.first_name} ${user.last_name}`.toLowerCase()
            : user.email?.toLowerCase() || "";
        return (
          userName.includes(searchText.toLowerCase()) ||
          user.email?.toLowerCase().includes(searchText.toLowerCase())
        );
      });
    }

    return filtered;
  }, [users, startDate, endDate, filter, searchText]);

  const sortedData = useMemo(
    () =>
      sortTableData(filteredData, sortName, sortDirection, {
        firstName: (user) =>
          user?.first_name && user?.last_name
            ? `${user.first_name} ${user.last_name}`
            : user?.email || "",
      }),
    [filteredData, sortName, sortDirection],
  );

  const columns = [
    {
      name: "firstName",
      label: "User Name",
      options: {
        customBodyRender: (value, tableMeta) => {
          const rowData = sortedData[tableMeta.rowIndex];
          const userName =
            rowData?.first_name && rowData?.last_name
              ? `${rowData.first_name} ${rowData.last_name}`
              : rowData?.email || `User #${rowData?.id || "-"}`;
          const userImage = rowData?.image_url || DefaultAvatar;
          const safeVal = userName.toLowerCase().replace(/\s+/g, "_");
          return (
            <div
              className={`col-clientName val-${safeVal} d-flex align-items-center gap-8`}
            >
              <img
                style={{
                  height: "35px",
                  width: "35px",
                  borderRadius: "50%",
                  objectFit: "cover",
                }}
                src={userImage}
                alt="user"
                onError={(e) => {
                  e.target.src = DefaultAvatar;
                }}
              />
              {userName}
            </div>
          );
        },
      },
    },
    {
      name: "created_at",
      label: "Date",
      options: {
        customBodyRender: (value) => {
          const formattedDate = formatDate(value);
          const safeVal = formattedDate.toLowerCase().replace(/\s+/g, "-");
          return (
            <span className={`col-date val-${safeVal} text-gray-600`}>
              {formattedDate}
            </span>
          );
        },
      },
    },
    {
      name: "email",
      label: "Email Address",
      options: {
        customBodyRender: (value) => {
          const safeVal = (value || "").toLowerCase().replace(/\s+/g, "-");
          return (
            <span className={`col-email val-${safeVal}`}>{value || "-"}</span>
          );
        },
      },
    },
    {
      name: "Manage",
      label: "Manage",
      options: {
        filter: false,
        sort: false,
        customBodyRenderLite: (dataIndex) => {
          const rowData = sortedData[dataIndex];
          const userName =
            rowData?.first_name && rowData?.last_name
              ? `${rowData.first_name} ${rowData.last_name}`
              : rowData?.email || `User #${rowData?.id || "-"}`;
          return (
            <div className="d-flex justify-content-center">
              <Icon
                onClick={() =>
                  navigate(`/edit-user/${rowData.id}`, {
                    state: getReturnState(),
                  })
                }
                className="editBtn hover:cursor-pointer"
                icon="line-md:edit"
                width="24"
                height="24"
                style={{ cursor: 'pointer' }}
              />
              <Icon
                onClick={() => handleDeleteUser(rowData.id, userName)}
                className="deleteBtn hover:cursor-pointer"
                icon="material-symbols:delete-outline"
                width="24"
                height="24"
                style={{ cursor: 'pointer' }}
              />
            </div>
          );
        },
      },
    },
  ];

  const options = {
    selectableRows: "none",
    rowsPerPage,
    rowsPerPageOptions: getRowsPerPageOptions(
      getPaginationTotal(usersPagination, filteredData.length || users.length)
    ),
    responsive: "standard",
    elevation: 0,
    print: false,
    download: false,
    viewColumns: false,
    filter: false,
    search: true,
    sortOrder: getSortOrder(sortName, sortDirection),
    onColumnSortChange: (changedColumn, direction) => {
      setTableState({
        sortName: direction === "none" ? "" : changedColumn,
        sortDirection: direction === "none" ? "" : direction,
      });
    },
    searchText: searchText,
    onSearchChange: (searchValue) => {
      setTableState({ searchText: searchValue || "", page: 0 });
    },
    page,
    onChangePage: (newPage) => {
      setTableState({ page: newPage });
    },
    onChangeRowsPerPage: (numberOfRows) => {
      setTableState({ rowsPerPage: numberOfRows, page: 0 });
    },
  };

  if (loading) {
    return (
      <div className="card basic-data-table">
        <div className="card-body text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3">Loading users...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card basic-data-table">
        <div className="card-body text-center py-5">
          <div className="text-danger">
            <Icon
              icon="material-symbols:error-outline"
              width="48"
              height="48"
            />
            <p className="mt-3">
              Error:{" "}
              {typeof error === "string" ? error : "Failed to load users"}
            </p>
            <button
              className="btn btn-primary mt-3"
              onClick={() => dispatch(getUsers({ page: page + 1, perPage: rowsPerPage }))}
              disabled={deletingUser}
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="card basic-data-table">
      <div className="card-header d-flex justify-content-end align-items-center flex-wrap gap-3">
        {/* <div className="d-flex align-items-center gap-2">
          <DatePicker
            selected={startDate}
            onChange={(date) => setStartDate(date)}
            selectsStart
            startDate={startDate}
            endDate={endDate}
            customInput={<CustomInput />}
            dateFormat="dd MMM yyyy"
          />
          <span>-</span>
          <DatePicker
            selected={endDate}
            onChange={(date) => setEndDate(date)}
            selectsEnd
            startDate={startDate}
            endDate={endDate}
            minDate={startDate}
            customInput={<CustomInput />}
            dateFormat="dd MMM yyyy"
          />
        </div> */}
        <div className="custom-toggle">
          <div className="toggle-container">
            <div
              className={`toggle-indicator ${
                filter === "all" ? "right" : "left"
              }`}
            />
            <div
              className={`toggle-option ${
                filter === "monthly" ? "active" : ""
              }`}
              onClick={() => setTableState({ filter: "monthly", page: 0 })}
            >
              <Icon icon="mdi:account" width="22" />
              <span>This Month</span>
            </div>
            <div
              className={`toggle-option ${filter === "all" ? "active" : ""}`}
              onClick={() => setTableState({ filter: "all", page: 0 })}
            >
              <Icon icon="mdi:account-group" width="25" />
              <span>All Time</span>
            </div>
          </div>
        </div>
      </div>
      <div className="card-body">
        <MUIDataTable
          data={sortedData}
          columns={columns}
          options={options}
          className="overflow-hidden packageTable"
        />
      </div>
    </div>
  );
};

export default UsersDataTable;
